import React from 'react';
import PropTypes from 'prop-types';
import { Col, Form } from 'react-bootstrap';
import {
  InputFile,
  FileButton,
  FileIcon,
  FileInputContainer,
  FileVideo
} from '../../user/styled';
import { t } from 'i18next';
import { useTextLanguage } from '../../../hooks/useShared';
import { useFile } from '../../../hooks/useFile';
import { useEdit, useEditFiles } from '../../../hooks/useEdit';
import { Error } from '../../shared/simple/Error';
import { Label } from '../../shared/simple/Label';
import { TooltipIcon } from '../../shared/simple/TooltipIcon';
import { useFormContext } from 'react-hook-form';
import documentIconSource from '../../../assets/images/shared/document-file-icon.svg';
import imageIconSource from '../../../assets/images/shared/camera-icon.svg';
import videoIconSource from '../../../assets/images/advance-tier/video-camera.svg';
import { useSwitchDesign } from '../../../hooks/useSwitchDesign';
import { Required } from '../../shared/Input/styled';

/**
 * File Input Button component
 * @component
 * @param {Object} field
 * @param {String} accept
 * @param {String} type
 * @param {String} formatAccept
 * @param {Boolean} isVisible    - Only to be used from the Fields component (in the rejected/expired document's view)
 * @param {Boolean} isNewDesign
 * @returns
 */
export const FileInputButton = ({ field, accept, type, formatAccept, isFileUploadable = true, isNewDesign = false }) => {
  const { description, languageText } = useTextLanguage();
  const { register, formState: { errors } } = useFormContext();
  const { onChange, uniqueName, fileSizeLimit } = useFile(field, type);
  const { colSize } = useEdit(field, isFileUploadable);
  useEditFiles(field, onChange);
  const { altErrorStyle } = useSwitchDesign()

  return (
    <>
      {field && (
        <>
          {(colSize?.includes('col-before')) && (
            <Col xs={12} key={`col_before_${field.id}`}></Col>
          )}
          <Col
            className={ `mt-4 ${colSize}` }
            data-module={ `module-${field?.category?.field_category}` }
            data-name={ uniqueName }
            id={ `group_field_${field.id}` }
            key={ field.id }
            xs={12}
          >
            <FileInputContainer className="mb-3">
              <Label htmlFor={ field.name || field.id } isNewDesign={isNewDesign}>
                { field[description] }{ field.required && (
                  <Required className="text-danger">&nbsp;*</Required>) }
                { field?.[`tooltip${languageText}`] && (
                  <TooltipIcon 
                    tooltipText={ field[`tooltip${languageText}`] } 
                    color={isNewDesign && 'third'}  
                  />) }
              </Label>
              <FileButton>
                <InputFile
                  { ...register(uniqueName) }
                  accept={ accept }
                  data-action={ field?.action }
                  data-category={ `category-${field?.category?.field_category}` }
                  data-condition={ field?.parent_condition }
                  data-parent={ field?.parent }
                  data-type-field={ field?.data_type }
                  data-unique-name={ field.unique_name }
                  id={ field.id }
                  key={ field.id }
                  name={ uniqueName }
                  onChange={ onChange }
                />
                <FileIcon
                  src={(type === "image")
                    ? imageIconSource
                    : (type === "document") ? documentIconSource : videoIconSource}
                  data-image={ `image-${field.unique_name}` }
                />
                <FileVideo
                  data-video={ `video-${field.unique_name}` }
                  className="d-none"
                />
              </FileButton>
              <Form.Text muted>
                <Required className="text-danger">&nbsp;*&nbsp;</Required>
                <Label isNewDesign={isNewDesign}>
                  { t("files.fileSizeLimit", { size: fileSizeLimit }) }
                </Label>
              </Form.Text><br />
              <Form.Text muted>
                <Label isNewDesign={isNewDesign}>
                  { t("files.formatAccept", { accept: formatAccept }) }
                </Label>
              </Form.Text><br />
              {errors?.[uniqueName]?.message && (
                <Error
                  message={ errors[uniqueName].message } 
                  altErrorMessageStyle={altErrorStyle}  
                />
              )}
            </FileInputContainer>
          </Col>
        </>
      )}
    </>
  );
};

FileInputButton.propTypes = {
  field: PropTypes.object,
  accept: PropTypes.string,
  type: PropTypes.string,
  formatAccept: PropTypes.string,
};